import React from "react";
import { Link } from "react-router-dom";
import { Icon, fmtCoins } from "../ui";
import { characterPic } from "../../assets/characterImages";
import { useToast } from "../../context/ToastContext";
import ChatIcon from "./ChatIcon";
import { UNAVAILABLE } from "./format";

const pct = (v) => `${Math.max(0, Math.min(100, (Math.max(0, Number(v) || 0) / 1000) * 100))}%`;

export function RelationBars({ rel, compact }) {
  return (
    <div className={`rel-bars${compact ? " rel-bars--compact" : ""}`}>
      <div className="rel-bar" title="ค่าความโรแมนติก">
        <div className="rel-bar__track rel-bar__track--love" role="progressbar" aria-label="ค่าความโรแมนติก" aria-valuenow={rel.romanticValue || 0} aria-valuemin={0} aria-valuemax={1000}><i style={{ width: pct(rel.romanticValue) }} /></div>
        <span><b>{rel.romanticValue || 0}</b> ความโรแมนติก</span>
      </div>
      <div className="rel-bar" title="ค่าความสนิท">
        <div className="rel-bar__track rel-bar__track--friend" role="progressbar" aria-label="ค่าความสนิท" aria-valuenow={rel.intimacyValue || 0} aria-valuemin={0} aria-valuemax={1000}><i style={{ width: pct(rel.intimacyValue) }} /></div>
        <span><b>{rel.intimacyValue || 0}</b> ความสนิท</span>
      </div>
    </div>
  );
}

function Row({ icon, label, value, onClick, badge, disabled }) {
  return (
    <button className="info-row" onClick={onClick} aria-disabled={disabled || undefined}>
      <ChatIcon name={icon} size={18} />
      <span className="grow info-row__label">{label}{badge ? <span className="chip chip--sm" style={{ marginLeft: 8 }}>{badge}</span> : null}</span>
      {value ? <span className="faint info-row__value clamp-1">{value}</span> : null}
      <ChatIcon name="chevron" size={16} className="faint" />
    </button>
  );
}

/** แผงข้อมูลแชท (ขวา/desktop หรือ sheet/mobile): ตัวละคร สถิติ ความสัมพันธ์ ตั้งค่า */
export default function ChatInfo({ info, model, onOpen, onToggle, onNewChat, onShare }) {
  const toast = useToast();
  const c = info.character || {};
  const st = info.stats || {};
  const rel = info.relationship || {};
  const soon = (what) => toast(`${what}: ${UNAVAILABLE}`, "info");
  const since = st.since ? new Date(st.since).toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "numeric" }) : "-";
  return (
    <div className="chat-info">
      <div className="info-hero info-hero--portrait">
        <img className="info-hero__img" src={characterPic({ image: info.image, id: info.characterId || info.name }, "med")} alt="" width="720" height="1080" decoding="async" />
        <div className="info-hero__txt">
          <div className="info-hero__name clamp-1">{c.name || info.name}</div>
          {info.creatorName ? <div className="info-hero__by clamp-1">ผู้สร้าง {info.creatorName}</div> : null}
        </div>
      </div>
      {c.tagline ? <p className="muted clamp-2 mt-8" style={{ fontSize: 13 }}>{String(c.tagline).replaceAll("{{user}}", "คุณ")}</p> : null}
      <div className="info-actions">
        {info.characterId ? <Link to={`/c/${info.characterId}`} className="info-action"><Icon name="user" size={18} />โปรไฟล์</Link> : null}
        <button className="info-action" onClick={onShare}><ChatIcon name="share" size={18} />แชร์</button>
        <Link to="/chat" className="info-action"><Icon name="chat" size={18} />แชทอื่น</Link>
        <button className="info-action" onClick={onNewChat}><Icon name="plus" size={18} />แชทใหม่</button>
      </div>

      <div className="info-stats">
        <div><span className="faint">ตั้งแต่</span><b>{since}</b></div>
        <div><span className="faint">คุยมา</span><b>{fmtCoins(st.days || 1)} วัน</b></div>
        <div><span className="faint">สตรีคปัจจุบัน</span><b className="streak"><ChatIcon name="flame" size={15} />{st.streakCount || 0} วัน</b></div>
        <div><span className="faint">สตรีคนานสุด</span><b>{st.streakBest || 0} วัน</b></div>
        <div><span className="faint">ข้อความ</span><b>{fmtCoins(st.messageCount || 0)}</b></div>
      </div>

      <div className="info-section">ตั้งค่าตัวละคร</div>
      <label className="switch-row">
        <span className="grow"><b>โหมดความสัมพันธ์</b><span className="faint" style={{ display: "block", fontSize: 12 }}>ให้ความสัมพันธ์ส่งผลต่อบทสนทนา (ทดลอง)</span></span>
        <input type="checkbox" role="switch" checked={!!info.relationshipMode} onChange={(e) => onToggle("relationshipMode", e.target.checked)} />
      </label>
      <div className="rel-card">
        <div className="rel-card__label"><Icon name="heart" size={16} filled /> {rel.romanticLabel || "เฉยๆ"} <span className="faint">· {rel.intimacyLabel || "คนรู้จัก"}</span></div>
        <RelationBars rel={rel} />
      </div>

      <div className="info-rows">
        <Row icon="user" label="บทบาทของฉัน" value={info.persona?.name || "ยังไม่ได้เลือก"} onClick={() => onOpen("persona")} />
        <Row icon="book" label="สถานการณ์" value={info.scenario?.name || "ยังไม่ได้เลือก"} onClick={() => onOpen("scenario")} />
        <Row icon="edit" label="โน้ตตัวละคร" value={info.memory?.charCount ? `${fmtCoins(info.memory.charCount)}/${fmtCoins(info.memoryLimit)}` : `ว่าง · สูงสุด ${fmtCoins(info.memoryLimit)}`} onClick={() => onOpen("note")} />
        <Row icon="sparkle" label="โมเดล AI" value={model ? `${model.name} · ${model.coinPerMessage} คอยน์` : ""} onClick={() => onOpen("model")} />
        <Row icon="gift" label="ส่งของขวัญ" onClick={() => onOpen("gift")} />
      </div>

      <div className="info-section">ตั้งค่าแชท</div>
      <div className="info-rows">
        <Row icon="x" label="คำที่ไม่ต้องการ" value={info.settings?.bannedWords?.length ? `${info.settings.bannedWords.length} คำ` : "ไม่มี"} onClick={() => onOpen("banned")} />
        <label className="switch-row">
          <span className="grow"><b>ระบบป้องกันภาษาปน</b><span className="faint" style={{ display: "block", fontSize: 12 }}>ลบภาษาอื่นที่หลุดมาในคำตอบ</span></span>
          <input type="checkbox" role="switch" checked={info.settings?.langGuard !== false} onChange={(e) => onToggle("langGuard", e.target.checked)} />
        </label>
        <Row icon="pencil" label="เปลี่ยนชื่อแชท" value={info.name} onClick={() => onOpen("rename")} />
        <Row icon="archive" label={info.isArchived ? "ย้ายกลับจากจัดเก็บ" : "จัดเก็บแชท"} onClick={() => onOpen("archive")} />
        <Row icon="trash" label="ลบแชท" onClick={() => onOpen("delete")} />
      </div>

      <div className="info-section">ฟีเจอร์ที่กำลังมา</div>
      <div className="info-rows">
        <Row icon="phone" label="โทรด้วยเสียง" badge={UNAVAILABLE} onClick={() => soon("โทรด้วยเสียง")} disabled />
        <Row icon="mic" label="เล่นเสียงข้อความ" badge={UNAVAILABLE} onClick={() => soon("เล่นเสียงข้อความ")} disabled />
        <Row icon="image" label="สร้างรูปภาพ" badge={UNAVAILABLE} onClick={() => soon("สร้างรูปภาพ")} disabled />
        <Row icon="cards" label="ดูดวงไพ่ทาโรต์" badge={UNAVAILABLE} onClick={() => soon("ดูดวงไพ่ทาโรต์")} disabled />
      </div>
    </div>
  );
}
